import { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion as m } from 'framer-motion';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff } from 'lucide-react';
import Navbar from '../components/Navbar';
import { base44 } from '../api/base44Client';



const CORRECT_PASSWORD = 'mcdonalds123';
const HANGMAN_WORDS = ['dumbo', 'obsession', 'fate', 'genz', 'clingy', 'history', 'respectfather'];
const WORD_PHRASES = {
  dumbo: 'why dumbos have big ears? `mbk — a srs q',
  clingy: 'be clingy always...',
  history: 'how to view google history?',
  obsession: 'obsession is a choice...',
  fate: 'fate...u decide it',
  genz: 'gen z energy...',
  familyalways: '(always and always)',
};

function FaceParticle({ id, x, onDone }) {
  return (
    <m.img
      src="https://media.base44.com/images/public/69f0e204b691b63890319b5a/81436a542_generated_image.png"
      className="fixed top-0 z-[9999] pointer-events-none rounded-full"
      style={{ left: `${x}%`, width: 36, height: 36, objectFit: 'cover' }}
      initial={{ y: -50, opacity: 1, rotate: 0, scale: 0.8 }}
      animate={{ y: '105vh', opacity: [1, 1, 0], rotate: Math.random() > 0.5 ? 200 : -200, scale: [0.8, 1.2, 0.7] }}
      transition={{ duration: 1.6 + Math.random() * 1.2, ease: 'easeIn' }}
      onAnimationComplete={onDone}
    />
  );
}
const MAX_WRONG = 6;

const HangmanDrawing = ({ wrong }) => (
  <svg viewBox="0 0 120 130" width="120" height="130" className="mx-auto">
    {/* Gallows */}
    <line x1="10" y1="125" x2="110" y2="125" stroke="hsl(var(--primary))" strokeWidth="2" opacity="0.4"/>
    <line x1="30" y1="125" x2="30" y2="5" stroke="hsl(var(--primary))" strokeWidth="2" opacity="0.4"/>
    <line x1="30" y1="5" x2="75" y2="5" stroke="hsl(var(--primary))" strokeWidth="2" opacity="0.4"/>
    <line x1="75" y1="5" x2="75" y2="20" stroke="hsl(var(--primary))" strokeWidth="2" opacity="0.4"/>
    {/* Head */}
    {wrong >= 1 && <circle cx="75" cy="30" r="10" stroke="hsl(var(--primary))" strokeWidth="2" fill="none"/>}
    {/* Body */}
    {wrong >= 2 && <line x1="75" y1="40" x2="75" y2="75" stroke="hsl(var(--primary))" strokeWidth="2"/>}
    {/* Left arm */}
    {wrong >= 3 && <line x1="75" y1="50" x2="55" y2="65" stroke="hsl(var(--primary))" strokeWidth="2"/>}
    {/* Right arm */}
    {wrong >= 4 && <line x1="75" y1="50" x2="95" y2="65" stroke="hsl(var(--primary))" strokeWidth="2"/>}
    {/* Left leg */}
    {wrong >= 5 && <line x1="75" y1="75" x2="55" y2="100" stroke="hsl(var(--primary))" strokeWidth="2"/>}
    {/* Right leg */}
    {wrong >= 6 && <line x1="75" y1="75" x2="95" y2="100" stroke="hsl(var(--primary))" strokeWidth="2"/>}
  </svg>
);

function HangmanGame({ onWin, nickname }) {
  const [guessed, setGuessed] = useState(new Set());
  const [done, setDone] = useState(false);
  const [particles, setParticles] = useState([]);
  const [showMsg, setShowMsg] = useState(false);
  const [position, setPosition] = useState(null);
  const [word, setWord] = useState(() => HANGMAN_WORDS[Math.floor(Math.random() * HANGMAN_WORDS.length)]);
  const wrong = [...guessed].filter(l => !word.includes(l)).length;
  const won = word.split('').every(l => guessed.has(l));
  const lost = wrong >= MAX_WRONG;

  useEffect(() => {
    if (won && !done) {
      setDone(true);
      onWin();
      setShowMsg(true);
      const spawn = (delay) => setTimeout(() => {
        setParticles(prev => [
          ...prev,
          ...Array.from({ length: 20 }, (_, i) => ({ id: Date.now() + i + delay, x: 2 + Math.random() * 96 }))
        ]);
      }, delay);
      spawn(0); spawn(400); spawn(800); spawn(1200);
      (async () => {
        await base44.entities.HangmanPlayer.create({ nickname: nickname || 'anon', result: 'won', wrong_guesses: wrong });
        const allWinners = await base44.entities.HangmanPlayer.filter({ result: 'won' });
        setPosition(allWinners.length);
      })();
    }
    if (lost && !done) {
      setDone(true);
      base44.entities.HangmanPlayer.create({ nickname: nickname || 'anon', result: 'lost', wrong_guesses: wrong });
    }
  }, [won, lost]);

  const guess = (letter) => {
    if (guessed.has(letter) || won || lost) return;
    setGuessed(prev => new Set([...prev, letter]));
  };

  const removeParticle = (id) => setParticles(prev => prev.filter(p => p.id !== id));

  const reset = () => { setGuessed(new Set()); setDone(false); setShowMsg(false); setWord(HANGMAN_WORDS[Math.floor(Math.random() * HANGMAN_WORDS.length)]); };

  const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('');

  return (
    <>
      <AnimatePresence>
        {particles.map(p => (
          <FaceParticle key={p.id} id={p.id} x={p.x} onDone={() => removeParticle(p.id)} />
        ))}
      </AnimatePresence>
      {showMsg && (
        <m.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="fixed inset-0 z-[9998] flex items-center justify-center pointer-events-none"
        >
          <div className="bg-card/90 backdrop-blur-xl border-2 border-primary/60 rounded-2xl px-8 py-5 text-center shadow-2xl shadow-primary/30">
            <p className="text-2xl font-extrabold text-primary mb-1" style={{ fontFamily: 'Syne, sans-serif' }}>too late...</p>
            <p className="font-mono-cv text-xs text-muted-foreground">try next week now 😭</p>
            <p className="font-mono-cv text-xs text-primary/70 mt-2 italic">{WORD_PHRASES[word]}</p>
          </div>
        </m.div>
      )}
      <div className="mt-8 bg-card border border-primary/20 rounded-2xl p-5 text-center max-w-sm mx-auto">
        <p className="font-mono-cv text-xs text-primary/60 mb-3 tracking-widest">HANGMAN — guess the word</p>
        {nickname && <p className="font-mono-cv text-xs text-muted-foreground mb-1">playing as: <span className="text-primary">{nickname}</span></p>}
        <HangmanDrawing wrong={wrong} />
        <div className="flex justify-center gap-1.5 flex-wrap mt-4 mb-4">
          {word.split('').map((l, i) => (
            <span key={i} className="w-6 border-b-2 border-primary/50 text-center font-mono-cv text-sm text-foreground">
              {guessed.has(l) ? l : ''}
            </span>
          ))}
        </div>
        {lost && (
          <div className="space-y-1">
            <p className="text-red-400 font-mono-cv text-xs">you lost</p>
            <p className="font-mono-cv text-xs text-primary/70 italic">{WORD_PHRASES[word]}</p>
          </div>
        )}
        {won && (
          <div className="space-y-1">
            <p className="text-primary font-mono-cv text-xs">u got it 👑</p>
            <p className="font-mono-cv text-xs text-primary/70 italic">{WORD_PHRASES[word]}</p>
          </div>
        )}
        <div className="flex flex-wrap justify-center gap-1 mb-3">
          {alphabet.map(l => (
            <button
              key={l}
              onClick={() => guess(l)}
              disabled={guessed.has(l) || won || lost}
              className={`w-7 h-7 rounded font-mono-cv text-xs transition-all ${
                guessed.has(l)
                  ? word.includes(l) ? 'bg-primary/20 text-primary' : 'bg-red-500/10 text-red-400/50'
                  : 'bg-secondary border border-border text-muted-foreground hover:border-primary/50 hover:text-primary'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        {(won || lost) && (
          <button onClick={reset} className="font-mono-cv text-xs text-primary border border-primary/30 px-3 py-1 rounded-lg hover:bg-primary/10 transition-all">play again</button>
        )}
      </div>
    </>
  );
}
const NOSTALGIC_PASSWORDS = ['Bilal518', 'Bilal518@', 'Bilal1734'];

export default function Journal() {
  const [input, setInput] = useState('');
  const [unlocked, setUnlocked] = useState(false);
  const [players, setPlayers] = useState([]);

  const [error, setError] = useState(false);
  const [nostalgic, setNostalgic] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [hangmanWon, setHangmanWon] = useState(false);
  const [btnPos, setBtnPos] = useState({ x: 0, y: 0 });
  const [nickname, setNickname] = useState(() => localStorage.getItem('hangman_nickname') || '');
  const [nicknameInput, setNicknameInput] = useState('');
  const [nicknameSet, setNicknameSet] = useState(() => !!localStorage.getItem('hangman_nickname'));
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestionText, setSuggestionText] = useState('');
  const [suggestionSubmitted, setSuggestionSubmitted] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [showAnswer, setShowAnswer] = useState(false);
  const [answerText, setAnswerText] = useState('');
  const [answerSubmitted, setAnswerSubmitted] = useState(false);

  const handleSetNickname = (e) => {
    e.preventDefault();
    const name = nicknameInput.trim();
    if (!name) return;
    localStorage.setItem('hangman_nickname', name);
    setNickname(name);
    setNicknameSet(true);
  };

  const handleSuggestionSubmit = async (e) => {
    e.preventDefault();
    if (!suggestionText.trim()) return;
    await base44.entities.Suggestion.create({ text: suggestionText, nickname: nickname || null });
    setSuggestionText('');
    setSuggestionSubmitted(true);
    setTimeout(() => setSuggestionSubmitted(false), 3000);
  };

  const handleAnswerSubmit = async (e) => {
    e.preventDefault();
    if (!answerText.trim()) return;
    await base44.entities.Answer.create({ content: answerText });
    setAnswerText('');
    setAnswerSubmitted(true);
    setTimeout(() => setAnswerSubmitted(false), 3000);
  };
  const btnRef = useRef(null);

  useEffect(() => {
    base44.entities.Answer.list('-created_date', 200).then(setAnswers);
    if (unlocked) {
      base44.entities.HangmanPlayer.list('-created_date', 200).then(setPlayers);
    }
  }, [unlocked]);

  const handleBtnMouseEnter = (e) => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const dx = cx - e.clientX;
    const dy = cy - e.clientY;
    const angle = Math.atan2(dy, dx);
    const jump = 160;
    const nx = Math.cos(angle) * jump;
    const ny = Math.sin(angle) * jump;
    setBtnPos(prev => ({ x: prev.x + nx, y: prev.y + ny }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input === CORRECT_PASSWORD) {
      setUnlocked(true);
      setError(false);
      setNostalgic(false);
      // Save successful access with nickname (or anonymous)
      base44.entities.HangmanPlayer.create({ 
        nickname: nickname || 'anonymous', 
        result: 'accessed', 
        wrong_guesses: 0 
      });
    } else if (NOSTALGIC_PASSWORDS.includes(input)) {
      setNostalgic(true);
      setError(false);
      setInput('');
    } else {
      setError(true);
      setNostalgic(false);
      // Save wrong password attempt with nickname (or anonymous if not set)
      base44.entities.Suggestion.create({ 
        text: `[WRONG PASSWORD]: ${input}`, 
        nickname: nickname || 'anonymous' 
      });
      setInput('');
    }
  };

  if (!unlocked) {
    return (
      <div className="min-h-screen bg-background dot-bg flex flex-col">
        <Navbar />

        <div className="flex-1 flex items-center justify-center px-8">
          <div className="w-full max-w-4xl flex flex-col md:flex-row gap-8 items-center justify-center">
            {/* Left: Lock section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-center"
            >
              <div className="relative w-16 h-16 mx-auto mb-6">
                <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-ping" />
                <div className="relative w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center">
                  <Lock size={28} className="text-primary" />
                </div>
              </div>
              <h2 className="text-3xl font-extrabold text-foreground mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>This is private...</h2>
              <p className="text-muted-foreground text-sm mb-8">enter the password to read my thoughts</p>
              
              {/* Nickname section - completely optional */}
              {!nickname && (
                <form onSubmit={handleSetNickname} className="mt-4 bg-card border border-primary/20 rounded-2xl p-5 max-w-sm mx-auto text-left">
                  <p className="font-mono-cv text-xs text-primary/60 mb-3 tracking-widest">HANGMAN — pick a nickname</p>
                  <input
                    type="text"
                    value={nicknameInput}
                    onChange={e => setNicknameInput(e.target.value)}
                    placeholder="your nickname..."
                    maxLength={20}
                    className="w-full bg-background border border-border rounded-xl px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground font-mono-cv outline-none focus:border-primary/60 transition-all mb-2"
                  />
                  <button type="submit" className="w-full bg-primary text-primary-foreground font-bold font-mono-cv text-xs py-2.5 rounded-xl hover:opacity-90 transition-all">set nickname</button>
                </form>
              )}
              
              {/* Show Hangman if nickname is set */}
              {nickname && !hangmanWon && (
                <HangmanGame onWin={() => setHangmanWon(true)} nickname={nickname} />
              )}
              
              {hangmanWon && (
                <p className="text-primary font-mono-cv text-xs mt-3 animate-pulse">🎉 hangman unlocked... now try the password lol</p>
              )}

              <form onSubmit={handleSubmit} className="flex flex-col gap-3 mt-6">
                <div className="relative">
                  <input
                    type={showPw ? 'text' : 'password'}
                    value={input}
                    onChange={e => { setInput(e.target.value); setError(false); }}
                    placeholder="password..."
                    className={`w-full bg-card border rounded-xl px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground font-mono-cv outline-none focus:border-primary/60 transition-all pr-10 ${
                      error ? 'border-red-500/60' : 'border-border'
                    }`}
                  />
                  <button type="button" onClick={() => setShowPw(!showPw)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {error && <p className="text-red-400 text-xs font-mono-cv">no shit, Sherlock?</p>}
                {nostalgic && <p className="text-yellow-400 text-xs font-mono-cv">crazyyy gng... it means you know me 😂</p>}
                <div ref={btnRef} onMouseEnter={handleBtnMouseEnter} style={{ transform: `translate(${btnPos.x}px, ${btnPos.y}px)`, transition: 'transform 0.2s cubic-bezier(0.25,0.46,0.45,0.94)' }}>
                  <button type="submit"
                    className="w-full bg-pink-500 text-white font-bold font-mono-cv text-xs py-2.5 rounded-xl hover:shadow-lg hover:shadow-pink-500/30">
                    submit
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background dot-bg flex flex-col">
      <Navbar />
      <div className="flex-1 flex items-center justify-center px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center"
        >
          <div className="relative w-16 h-16 mx-auto mb-6">
            <div className="absolute inset-0 rounded-2xl bg-primary/20 animate-ping" />
            <div className="relative w-16 h-16 rounded-2xl bg-primary/10 border border-primary/30 flex items-center justify-center">
              <Lock size={28} className="text-primary" />
            </div>
          </div>
          <h2 className="text-3xl font-extrabold text-foreground mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>This is private...</h2>
          <p className="text-muted-foreground text-sm max-w-xs mx-auto">
            my thoughts aren't public yet... maybe someday.
          </p>
          <div className="mt-10 bg-card border border-primary/20 rounded-2xl p-5 max-w-sm mx-auto text-left">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono-cv text-xs text-primary/60 tracking-widest">HANGMAN PLAYERS ({players.length})</p>
              <button onClick={() => base44.entities.HangmanPlayer.list('-created_date', 200).then(setPlayers)} className="font-mono-cv text-[10px] text-primary/50 hover:text-primary transition-colors">refresh</button>
            </div>
            {players.length === 0 ? (
              <p className="font-mono-cv text-xs text-muted-foreground">no players yet...</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {players.map((p, i) => (
                  <div key={p.id} className="flex items-center justify-between gap-3">
                    <span className="font-mono-cv text-xs text-muted-foreground">#{i + 1}</span>
                    <span className="font-mono-cv text-sm text-foreground flex-1">{p.nickname}</span>
                    <span className={`font-mono-cv text-xs px-2 py-0.5 rounded-full border ${
                      p.result === 'won' ? 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30' : 'text-red-400 bg-red-400/10 border-red-400/30'
                    }`}>{p.result}</span>
                    {p.wrong_guesses != null && <span className="font-mono-cv text-xs text-muted-foreground">{p.wrong_guesses}❌</span>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-10 bg-card border border-primary/20 rounded-2xl p-5 max-w-sm mx-auto text-left mb-8">
            <div className="flex items-center justify-between mb-3">
              <p className="font-mono-cv text-xs text-primary/60 tracking-widest">ANSWERS ({answers.length})</p>
              <button onClick={() => base44.entities.Answer.list('-created_date', 200).then(setAnswers)} className="font-mono-cv text-[10px] text-primary/50 hover:text-primary transition-colors">refresh</button>
            </div>
            {answers.length === 0 ? (
              <p className="font-mono-cv text-xs text-muted-foreground">no answers yet...</p>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {answers.map((a, i) => (
                  <div key={a.id} className="border-l-2 border-primary/20 pl-3 py-1.5">
                    <p className="font-mono-cv text-xs text-muted-foreground/60">#{i + 1}</p>
                    <p className="font-mono-cv text-xs text-foreground mt-0.5 leading-relaxed">{a.content}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-8 space-y-3">
            {!showSuggestion ? (
              <button
                onClick={() => setShowSuggestion(true)}
                className="w-full font-mono-cv text-xs text-primary border border-primary/30 bg-primary/5 px-4 py-2 rounded-lg hover:bg-primary/10 transition-all"
              >
                got a suggestion?
              </button>
            ) : (
              <form onSubmit={handleSuggestionSubmit} className="bg-card border border-primary/20 rounded-xl p-4">
                <textarea
                  value={suggestionText}
                  onChange={e => setSuggestionText(e.target.value)}
                  placeholder="drop ur suggestion..."
                  maxLength={500}
                  className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground font-mono-cv outline-none focus:border-primary/60 transition-all resize-none h-24 mb-2"
                />
                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={!suggestionText.trim()}
                    className="flex-1 bg-primary text-primary-foreground font-bold font-mono-cv text-xs py-2 rounded-lg hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    send
                  </button>
                  <button
                    type="button"
                    onClick={() => { setShowSuggestion(false); setSuggestionText(''); }}
                    className="flex-1 border border-border text-muted-foreground font-mono-cv text-xs py-2 rounded-lg hover:border-primary/40 transition-all"
                  >
                    cancel
                  </button>
                </div>
              </form>
            )}
            {suggestionSubmitted && (
              <m.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="bg-card/90 backdrop-blur-xl border border-primary/40 rounded-lg px-4 py-3 text-center"
              >
                <p className="font-mono-cv text-xs text-primary">u really think i would take ur suggestion? 😭</p>
              </m.div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
}